import React, { useRef, useEffect, useState } from 'react';

interface CyberParticlesProps {
  color?: string;
  particleCount?: number;
  connectionDistance?: number;
  particleSize?: number;
  particleSpeed?: number;
  mouseInteraction?: boolean;
  className?: string;
}

const CyberParticles: React.FC<CyberParticlesProps> = ({
  color = '#00ff00',
  particleCount = 70,
  connectionDistance = 150,
  particleSize = 2,
  particleSpeed = 0.5,
  mouseInteraction = true,
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number | undefined>(undefined);
  const resizeObserverRef = useRef<ResizeObserver | undefined>(undefined);
  const mousePosition = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
  const [particles, setParticles] = useState<Array<{
    x: number;
    y: number;
    dx: number;
    dy: number;
    size: number;
  }>>([]);
  
  // Generate the initial set of particles
  const generateParticles = (width: number, height: number) => {
    const newParticles = [];
    
    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        dx: (Math.random() - 0.5) * particleSpeed,
        dy: (Math.random() - 0.5) * particleSpeed,
        size: Math.random() * particleSize + 1
      });
    }
    
    setParticles(newParticles);
  };
  
  // Draw a single particle on the canvas
  const drawParticle = (
    ctx: CanvasRenderingContext2D, 
    particle: { x: number; y: number; size: number },
    mouseDistance: number = Infinity
  ) => {
    const { x, y, size } = particle;
    
    // Create gradient for each particle
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, size * 2);
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, 'transparent');
    
    // Draw the particle
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
    
    // If close to mouse, add glow effect
    if (mouseDistance < connectionDistance * 1.5) {
      const glowIntensity = 1 - (mouseDistance / (connectionDistance * 1.5));
      ctx.shadowBlur = 10 * glowIntensity;
      ctx.shadowColor = color;
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
  };
  
  // Draw connection lines between particles
  const drawConnections = (
    ctx: CanvasRenderingContext2D,
    particles: Array<{ x: number; y: number }>
  ) => {
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x;
        const dy = particles[i].y - particles[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < connectionDistance) {
          const opacity = 1 - (distance / connectionDistance);
          ctx.beginPath();
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(particles[j].x, particles[j].y);
          ctx.strokeStyle = `${color.replace('rgb', 'rgba').replace(')', `, ${opacity * 0.5})`)}`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }
    }
  };
  
  // Connect particles near the mouse
  const drawMouseConnections = (
    ctx: CanvasRenderingContext2D,
    particles: Array<{ x: number; y: number }>,
    mouseX: number,
    mouseY: number
  ) => {
    for (let i = 0; i < particles.length; i++) {
      const dx = particles[i].x - mouseX;
      const dy = particles[i].y - mouseY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < connectionDistance) {
        const opacity = 1 - (distance / connectionDistance);
        ctx.beginPath();
        ctx.moveTo(particles[i].x, particles[i].y);
        ctx.lineTo(mouseX, mouseY);
        ctx.strokeStyle = `${color.replace('rgb', 'rgba').replace(')', `, ${opacity * 0.8})`)}`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
  };
  
  // Animation loop
  const animate = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Update and draw particles
    const updatedParticles = particles.map(particle => {
      // Update particle position
      let { x, y, dx, dy, size } = particle;
      x += dx;
      y += dy;
      
      // Bounce off edges
      if (x < 0 || x > canvas.width) dx = -dx;
      if (y < 0 || y > canvas.height) dy = -dy;
      
      // Calculate distance from mouse for interactive effects
      const mouseX = mousePosition.current.x;
      const mouseY = mousePosition.current.y;
      const mouseDistance = Math.sqrt(
        Math.pow(x - mouseX, 2) + Math.pow(y - mouseY, 2)
      );
      
      // Draw the particle
      drawParticle(ctx, { x, y, size }, mouseDistance);
      
      // Add subtle attraction to mouse
      if (mouseInteraction && mouseDistance < connectionDistance * 2) {
        const forceDirection = {
          x: (mouseX - x) / mouseDistance,
          y: (mouseY - y) / mouseDistance
        };
        
        const forceMagnitude = 0.05 * (1 - mouseDistance / (connectionDistance * 2));
        
        dx += forceDirection.x * forceMagnitude;
        dy += forceDirection.y * forceMagnitude;
        
        // Limit speed
        const speed = Math.sqrt(dx * dx + dy * dy);
        if (speed > particleSpeed * 2) {
          dx = (dx / speed) * particleSpeed * 2;
          dy = (dy / speed) * particleSpeed * 2;
        }
      }
      
      return { x, y, dx, dy, size };
    });
    
    // Draw connections between particles
    drawConnections(ctx, updatedParticles);
    
    // Draw connections to mouse
    if (mouseInteraction) {
      drawMouseConnections(
        ctx, 
        updatedParticles, 
        mousePosition.current.x, 
        mousePosition.current.y
      );
    }
    
    // Update state for next frame
    setParticles(updatedParticles);
    
    // Continue animation loop
    animationFrameId.current = requestAnimationFrame(animate);
  };
  
  // Capture mouse movement
  const handleMouseMove = (e: MouseEvent) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    mousePosition.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };
  
  // Set up and clean up
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Set initial canvas size
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
    
    // Generate initial particles
    generateParticles(canvas.width, canvas.height);
    
    // Set up event listeners
    if (mouseInteraction) {
      window.addEventListener('mousemove', handleMouseMove);
    }
    
    // Set up resize observer
    const resizeObserver = new ResizeObserver(() => {
      if (canvas) {
        canvas.width = canvas.clientWidth;
        canvas.height = canvas.clientHeight;
        generateParticles(canvas.width, canvas.height);
      }
    });
    
    resizeObserver.observe(canvas);
    resizeObserverRef.current = resizeObserver;
    
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      
      if (mouseInteraction) {
        window.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);
  
  // Start animation when particles are generated
  useEffect(() => {
    if (particles.length > 0) {
      animationFrameId.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [particles]);
  
  return (
    <canvas
      ref={canvasRef}
      className={`background-canvas cyber-particles ${className}`}
      style={{
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none'
      }}
    />
  );
};

export default CyberParticles;